import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Mail,
  MessageSquare,
  RefreshCw,
  Sparkles,
  Search,
  Phone,
  CheckCircle2,
  ShieldCheck,
  Eye,
} from 'lucide-react';
import Layout from '../../components/layout/layout';
import Section from '../../components/ui/section';
import SectionBackground from '../../components/sections/section-background';
import { WebPageSchema, BreadcrumbSchema } from '../../lib/utils/schema-utils';

declare global {
  interface Window {
    RTWidget: unknown;
    Chatbase: {
      initIframe: (options: {
        chatbotId: string;
        domain: string;
        iframe: HTMLIFrameElement;
      }) => void;
    };
  }
}

const gradientText =
  'text-transparent bg-clip-text bg-gradient-to-r from-[#d03484] to-[#d47254]';
const eyebrowClass =
  'inline-flex items-center rounded-full bg-white px-5 py-2 text-sm font-semibold text-neutral-500 uppercase tracking-widest shadow-sm ring-1 ring-neutral-200 mb-6';
const h2Class =
  'text-4xl md:text-5xl font-bold tracking-tight text-[#30475e] mb-5 leading-[1.08]';
const h3Class = 'text-xl font-semibold text-[#30475e] mb-3 leading-snug';
const bodyClass = 'text-base text-neutral-600 leading-relaxed';
const sectionSubtitleClass =
  'text-lg md:text-xl text-neutral-600 leading-relaxed';

const candidateCommitments = [
  {
    icon: Mail,
    title: 'Réponse à 100 % des candidatures sous 48 h',
    description:
      "Aucune exception. Aucun ghosting. Même un refus est une réponse soignée — pour que vous puissiez continuer votre recherche sereinement.",
  },
  {
    icon: MessageSquare,
    title: 'Brief honnête sur le poste, l’équipe, le contexte',
    description:
      "Pas de marketing maquillé, pas de promesse vague. Vous recevez la vraie réalité — y compris ce qui peut vous freiner — pour décider en adulte.",
  },
  {
    icon: RefreshCw,
    title: 'Debrief structuré à chaque étape',
    description:
      "Vous savez pourquoi vous avancez, ou pourquoi vous vous arrêtez. Avec des éléments concrets. Pour que vous progressiez, même si ce n'est pas pour cette fois.",
  },
  {
    icon: Sparkles,
    title: 'Parcours Découverte avant l’entretien final',
    description:
      "Pas de surprise après la signature, plus de regret six mois après. Vous voyez la réalité avant de vous engager — et n'avancez que si vous êtes vraiment aligné.",
  },
];

const candidateMethod = [
  {
    icon: Search,
    title: 'Vous postulez',
    description:
      "Sur une offre listée plus haut, ou en candidature spontanée si votre profil colle à l'une de nos missions en cours.",
  },
  {
    icon: Phone,
    title: 'Pré-qualification 30 min',
    description:
      "Échange humain avec un consultant senior. On vous explique vraiment le poste, l'équipe, le contexte. Pas un screening junior à 18 questions.",
  },
  {
    icon: Eye,
    title: 'Plateforme Parcours Découverte',
    description:
      "Vous traversez un environnement digital sur-mesure : culture, équipe, mission, grille de rémunération. Avant le premier entretien client.",
  },
  {
    icon: CheckCircle2,
    title: 'Vous décidez en connaissance de cause',
    description:
      "Re-engagement explicite à la sortie du parcours. Ou retrait serein. Sans pression, sans culpabilité, sans casser la relation pour la suite.",
  },
];

export default function OffresPage() {
  const navigate = useNavigate();
  const pageUrl = 'https://recruteurs.io/candidat/offres';

  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://jobs-widget.recruiteecdn.com/widget.js';
    script.async = true;
    script.onload = () => {
      if (window.RTWidget) {
        new (window.RTWidget as new (config: unknown) => void)({
          companies: [114974],
          detailsMode: 'popup',
          language: 'fr',
          themeVars: {
            primary: '#30475e',
            secondary: '#d03484',
            text: '#30475e',
            textDark: '#121317',
            fontFamily:
              '"Helvetica Neue", Helvetica, Arial, "Lucida Grande", sans-serif;',
            baseFontSize: '16px',
          },
          flags: {
            showLocation: true,
            showCountry: true,
            showCity: true,
            showState: true,
            showLocationName: true,
            showWorkModel: true,
            groupByLocation: false,
            groupByState: true,
            groupByDepartment: false,
            groupByCompany: false,
          },
        });
      }
    };
    document.body.appendChild(script);

    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, []);

  const scrollToOffres = () => {
    document.getElementById('offres-list')?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  };

  return (
    <Layout
      title="Offres d'emploi cadre Marseille, Aix, PACA | recruteurs.io"
      description="Toutes nos offres d'emploi cadre à Marseille, Aix-en-Provence et en PACA. Process candidat respectueux, Plateforme Parcours Découverte avant signature, réponse 100% sous 48h."
      keywords="offres emploi marseille, offres emploi aix-en-provence, offres emploi cadre paca, recrutement marseille, jobs marseille"
    >
      <WebPageSchema
        page={{
          title: "Offres d'emploi cadre à Marseille, Aix, PACA | recruteurs.io",
          description:
            "Toutes nos offres d'emploi cadre. Process candidat respectueux, Plateforme Parcours Découverte avant signature, réponse 100% sous 48h.",
          url: pageUrl,
          lastReviewed: '2026-05-10',
        }}
      />

      <BreadcrumbSchema
        items={[
          { name: 'Accueil', url: 'https://recruteurs.io' },
          { name: 'Candidat', url: pageUrl },
          { name: "Offres d'emploi", url: pageUrl },
        ]}
      />

      {/* HERO — pastel candidat + image fond */}
      <SectionBackground
        page="offres"
        section="o-hero"
        className="relative overflow-hidden bg-gradient-to-br from-[#fff1f2] via-[#fef9c3] to-[#e0f2ff]"
      >
        <div
          aria-hidden
          className="absolute inset-0 z-0 pointer-events-none select-none flex items-center justify-center"
        >
          <img
            src="https://ygkvhunsrhvgddngoulm.supabase.co/storage/v1/object/public/backgrounds/backgrounds/offres-emplois-marseille.webp"
            alt=""
            className="w-full h-full object-cover opacity-15 md:opacity-20"
            fetchpriority="high"
            loading="eager"
            decoding="async"
          />
          <div className="absolute inset-0 bg-white/30" />
        </div>

        <section className="relative z-10 min-h-[88vh] flex items-center py-10 md:py-12 lg:py-14">
          <div className="w-full px-[5vw] xl:px-[100px] max-w-[1440px] mx-auto">
            <div className="max-w-4xl mx-auto text-center">
              <p className="text-xs md:text-sm font-semibold text-neutral-500 mb-3 leading-snug uppercase tracking-widest">
                Offres d'emploi · Marseille · Aix · PACA
              </p>

              <h1 className="text-3xl md:text-4xl lg:text-5xl xl:text-[3.5rem] font-title font-extrabold leading-[1.1] pb-1 mb-0">
                <span className="text-[#30475e]">Le recrutement mérite mieux. </span>
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#d03484] to-[#d47254]">
                  Les candidats aussi.
                </span>
              </h1>

              <p className="mt-5 mb-8 text-neutral-800 text-base md:text-lg leading-relaxed max-w-2xl mx-auto">
                On ne joue pas avec les gens. On conçoit une expérience de
                recrutement <strong>humaine, claire, exigeante mais
                respectueuse</strong> — parce que les talents d'aujourd'hui ne
                veulent plus être traités comme des CV sur pattes.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <button
                  onClick={scrollToOffres}
                  className="inline-flex items-center gap-2 rounded-2xl bg-gradient-to-r from-[#d03484] to-[#d47254] px-6 py-2.5 text-sm font-bold whitespace-nowrap text-white shadow-lg shadow-[#d03484]/20 hover:shadow-xl hover:-translate-y-0.5 transition-all"
                >
                  <Search className="w-5 h-5" />
                  Voir les offres en cours
                </button>
                <button
                  onClick={() => navigate('/candidat/pourquoi')}
                  className="inline-flex items-center gap-2 rounded-2xl bg-white text-[#30475e] px-6 py-2.5 text-sm font-bold whitespace-nowrap ring-1 ring-neutral-200 hover:ring-neutral-300 hover:-translate-y-0.5 transition-all"
                >
                  Pourquoi candidater chez nous
                </button>
              </div>
            </div>
          </div>
        </section>
      </SectionBackground>

      {/* SECTION 2 — Liste des offres (widget Recruitee) */}
      <SectionBackground page="offres" section="o-list" className="bg-white">
        <Section>
          <div
            id="offres-list"
            className="max-w-4xl mx-auto text-center mb-12 scroll-mt-24"
          >
            <span className={eyebrowClass}>Nos opportunités du moment</span>
            <h2 className={h2Class}>
              Toutes nos offres,{' '}
              <span className={gradientText}>en un coup d'œil.</span>
            </h2>
            <p className={sectionSubtitleClass}>
              Cadres, commerciaux, IT, retail, fonctions support. Cliquez sur
              une offre pour voir le détail et postuler. Réponse écrite à 100 %
              des candidatures sous 48 h ouvrées.
            </p>
          </div>

          <div className="max-w-5xl mx-auto rounded-[2rem] bg-[#fbfaf8] p-6 md:p-8 ring-1 ring-neutral-200">
            <div
              id="recruitee-careers"
              className="recruitee-jobs-wrapper jobs-listing"
            />
          </div>
        </Section>
      </SectionBackground>

      {/* SECTION 3 — Engagements candidat */}
      <SectionBackground page="offres" section="o-commit" className="bg-[#fbfaf8]">
        <Section>
          <div className="max-w-4xl mx-auto text-center mb-14">
            <span className={eyebrowClass}>Ce qu'on s'engage à faire</span>
            <h2 className={h2Class}>
              Quatre promesses concrètes.{' '}
              <span className={gradientText}>Opposables. Vérifiables.</span>
            </h2>
            <p className={sectionSubtitleClass}>
              Pas de promesses générales. Des engagements précis, vérifiables,
              opposables si nous y manquons.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            {candidateCommitments.map((item, index) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-80px' }}
                  transition={{ duration: 0.5, delay: index * 0.06 }}
                  className="rounded-[1.75rem] bg-white p-8 ring-1 ring-neutral-200 hover:-translate-y-1 hover:shadow-lg transition-all duration-300"
                >
                  <div className="w-12 h-12 rounded-2xl bg-gradient-to-r from-[#d03484] to-[#d47254] flex items-center justify-center text-white mb-5 shadow-lg shadow-[#d03484]/20">
                    <Icon className="w-6 h-6" />
                  </div>
                  <h3 className={h3Class}>{item.title}</h3>
                  <p className={bodyClass}>{item.description}</p>
                </motion.div>
              );
            })}
          </div>
        </Section>
      </SectionBackground>

      {/* SECTION 4 — Méthode candidat */}
      <SectionBackground page="offres" section="o-method" className="bg-white">
        <Section>
          <div className="max-w-4xl mx-auto text-center mb-14">
            <span className={eyebrowClass}>Comment ça se passe pour vous</span>
            <h2 className={h2Class}>
              Quatre étapes,{' '}
              <span className={gradientText}>traitées avec respect.</span>
            </h2>
            <p className={sectionSubtitleClass}>
              Du clic «&nbsp;Postuler&nbsp;» à la décision finale. Vous gardez la
              main, vous avez l'info, vous décidez.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {candidateMethod.map((step, index) => {
              const Icon = step.icon;
              return (
                <motion.div
                  key={step.title}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-80px' }}
                  transition={{ duration: 0.6, delay: index * 0.08 }}
                  className="group rounded-[1.75rem] bg-white p-8 shadow-sm ring-1 ring-neutral-200 hover:-translate-y-1 hover:shadow-xl transition-all duration-300"
                >
                  <div className="mb-6 flex items-center justify-between">
                    <div className="h-12 w-12 rounded-2xl bg-gradient-to-r from-[#d03484] to-[#d47254] flex items-center justify-center text-white font-bold shadow-lg shadow-[#d03484]/20">
                      0{index + 1}
                    </div>
                    <Icon className="w-5 h-5 text-neutral-300 group-hover:text-[#d03484] transition-colors duration-300" />
                  </div>
                  <h3 className={h3Class}>{step.title}</h3>
                  <p className={bodyClass}>{step.description}</p>
                </motion.div>
              );
            })}
          </div>
        </Section>
      </SectionBackground>

      {/* SECTION 5 — CTA final */}
      <SectionBackground page="offres" section="o-cta" className="bg-[#fbfaf8]">
        <Section>
          <div className="max-w-3xl mx-auto text-center">
            <h2 className={h2Class}>
              Pas l'offre de vos rêves dans la liste ?{' '}
              <span className={gradientText}>Préparez la suite.</span>
            </h2>
            <p className={`${sectionSubtitleClass} mb-10`}>
              Notre Coach Talent AI vous accompagne sur la préparation de vos
              entretiens, et notre Mapping RH vous aide à comprendre la
              compatibilité entre votre profil et l'équipe d'une entreprise
              avant même de candidater.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <button
                onClick={() => navigate('/candidat/talentai')}
                className="inline-flex items-center gap-2 rounded-2xl bg-gradient-to-r from-[#d03484] to-[#d47254] px-6 py-2.5 text-sm font-bold whitespace-nowrap text-white shadow-lg shadow-[#d03484]/20 hover:shadow-xl hover:-translate-y-0.5 transition-all"
              >
                <Sparkles className="w-5 h-5" />
                Coach Talent AI
              </button>
              <button
                onClick={() => navigate('/ressources/mapping-rh')}
                className="inline-flex items-center gap-2 rounded-2xl bg-white text-[#30475e] px-6 py-2.5 text-sm font-bold whitespace-nowrap ring-1 ring-neutral-200 hover:ring-neutral-300 hover:-translate-y-0.5 transition-all"
              >
                <ShieldCheck className="w-5 h-5" />
                Tester le Mapping RH
              </button>
            </div>
          </div>
        </Section>
      </SectionBackground>
    </Layout>
  );
}
